import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Bot, Plus, Check, ShieldCheck, Zap, X, Sliders,
    ShieldAlert, GitBranch, ZoomIn, ZoomOut, Maximize2,
    Save, Send, ChevronLeft, Trash2, GripVertical, Move
} from "lucide-react";

export default function WorkflowDesigner() {
    const [workflow, setWorkflow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [zoom, setZoom] = useState(1);
    const [toast, setToast] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);

    // AI Copilot state
    const [copilotOpen, setCopilotOpen] = useState(true);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content: "I'm your QueryTel Workflow Assistant. Ask me to add containment actions, configure FortiGate blocklists, or insert approval gates."
        }
    ]);
    const [inputPrompt, setInputPrompt] = useState("");
    const [copilotLoading, setCopilotLoading] = useState(false);

    // Modal state for adding action/condition
    const [addNodeType, setAddNodeType] = useState(null); // 'action' | 'condition'
    const [targetBranch, setTargetBranch] = useState("main"); // 'main' | 'true' | 'false'

    // Drag-and-Drop state
    const [draggingNodeId, setDraggingNodeId] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const canvasRef = useRef(null);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchWorkflow();
    }, []);

    const fetchWorkflow = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/respond/workflows/designer/wf-create-case-on-investigation`, { headers });
            const data = res.data;
            
            // Assign default layout coordinates if none exist
            const initialNodes = (data.nodes || []).map((node, idx) => {
                if (node.x !== undefined && node.y !== undefined) return node;
                
                // Default clean flowchart coordinates
                if (node.type === "trigger") {
                    return { ...node, x: 260, y: 30 };
                } else if (node.id === "n-create-case" || idx === 1) {
                    return { ...node, x: 260, y: 140 };
                } else if (node.type === "condition" || idx === 2) {
                    return { ...node, x: 250, y: 250 };
                } else if (node.branch === "true" || idx === 3) {
                    return { ...node, x: 100, y: 390 };
                } else if (node.branch === "false" || idx === 4) {
                    return { ...node, x: 420, y: 390 };
                } else {
                    return { ...node, x: 260, y: 520 + (idx - 5) * 110 };
                }
            });

            setWorkflow({ ...data, nodes: initialNodes });
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load workflow designer:", err);
        }
        setLoading(false);
    };

    // ─── Drag & Drop Event Handlers ───
    const handleNodeMouseDown = (e, nodeId) => {
        e.stopPropagation();
        const node = workflow?.nodes?.find(n => n.id === nodeId);
        if (!node) return;

        const canvasRect = canvasRef.current?.getBoundingClientRect();
        if (!canvasRect) return;

        setDraggingNodeId(nodeId);
        setDragOffset({
            x: (e.clientX - canvasRect.left) / zoom - (node.x || 0),
            y: (e.clientY - canvasRect.top) / zoom - (node.y || 0)
        });
    };

    const handleCanvasMouseMove = (e) => {
        if (!draggingNodeId || !canvasRef.current) return;

        const canvasRect = canvasRef.current.getBoundingClientRect();
        const newX = Math.round(((e.clientX - canvasRect.left) / zoom - dragOffset.x) / 10) * 10;
        const newY = Math.round(((e.clientY - canvasRect.top) / zoom - dragOffset.y) / 10) * 10;

        setWorkflow(prev => ({
            ...prev,
            nodes: prev.nodes.map(n => n.id === draggingNodeId ? { ...n, x: Math.max(20, newX), y: Math.max(20, newY) } : n)
        }));
    };

    const handleCanvasMouseUp = () => {
        if (draggingNodeId) {
            setDraggingNodeId(null);
        }
    };

    // ─── Send Message to Copilot ───
    const handleSendMessage = async (promptText) => {
        const query = promptText || inputPrompt;
        if (!query.trim()) return;

        const newMessages = [...messages, { role: "user", content: query }];
        setMessages(newMessages);
        setInputPrompt("");
        setCopilotLoading(true);

        try {
            const res = await axios.post(
                `${backendURL}/api/respond/workflows/designer/copilot`,
                { query, currentWorkflowId: workflow?.id },
                { headers }
            );
            setMessages([...newMessages, { role: "assistant", content: res.data.answer, suggestedAction: res.data.suggestedAction }]);
        } catch (err) {
            console.error("Copilot error:", err);
        }
        setCopilotLoading(false);
    };

    // ─── Save Draft ───
    const handleSaveDraft = async () => {
        try {
            const res = await axios.put(
                `${backendURL}/api/respond/workflows/designer/${workflow?.id}`,
                {
                    nodes: workflow.nodes,
                    name: workflow.name,
                    enabled: workflow.enabled,
                    version: workflow.version || "1.2.0"
                },
                { headers }
            );
            if (res.data.success) {
                setToast("Workflow Draft Saved to Database");
                setTimeout(() => setToast(null), 2500);
            }
        } catch (err) {
            console.error("Save draft error:", err);
            setToast("Failed to save draft");
            setTimeout(() => setToast(null), 2500);
        }
    };

    // ─── Publish ───
    const handlePublish = async () => {
        try {
            const res = await axios.post(
                `${backendURL}/api/respond/workflows/designer/${workflow?.id}/publish`,
                {},
                { headers }
            );
            setToast(res.data.message || "Workflow Published!");
            setTimeout(() => setToast(null), 3500);
        } catch (err) {
            console.error("Publish error:", err);
        }
    };

    // ─── Add Node (Action / Condition) ───
    const handleAddNode = async (nodeTemplate) => {
        try {
            // Calculate next position on canvas
            const existingNodes = workflow?.nodes || [];
            let posX = 260;
            let posY = 400;

            if (targetBranch === "true") {
                posX = 100;
                posY = 500 + existingNodes.filter(n => n.branch === "true").length * 100;
            } else if (targetBranch === "false") {
                posX = 420;
                posY = 500 + existingNodes.filter(n => n.branch === "false").length * 100;
            } else {
                posY = 120 + existingNodes.length * 90;
            }

            const newNodePayload = {
                title: nodeTemplate.title,
                subtitle: nodeTemplate.subtitle,
                description: nodeTemplate.desc || nodeTemplate.description,
                type: nodeTemplate.type || "action",
                color: nodeTemplate.color || "purple",
                connector: nodeTemplate.connector || nodeTemplate.subtitle,
                branch: targetBranch !== "main" ? targetBranch : null,
                x: posX,
                y: posY
            };

            const res = await axios.post(
                `${backendURL}/api/respond/workflows/designer/${workflow?.id}/node`,
                newNodePayload,
                { headers }
            );

            if (res.data.success) {
                setWorkflow(prev => ({
                    ...prev,
                    nodes: [...prev.nodes, res.data.node]
                }));
                setAddNodeType(null);
                setToast(`Added node: ${nodeTemplate.title}`);
                setTimeout(() => setToast(null), 2500);
            }
        } catch (err) {
            console.error("Add node error:", err);
        }
    };

    // ─── Delete Entire Workflow from Designer ───
    const handleDeleteWorkflow = async () => {
        if (!workflow?.id) {
            setToast("No workflow loaded to delete");
            setTimeout(() => setToast(null), 2500);
            return;
        }

        const confirmed = window.confirm(`Are you sure you want to permanently delete the entire workflow "${workflow.name || workflow.id}"?`);
        if (!confirmed) return;

        try {
            const currentToken = localStorage.getItem("token") || token;
            await axios.delete(`${backendURL}/api/respond/workflows/${workflow.id}`, {
                headers: { Authorization: `Bearer ${currentToken}` }
            });
            setToast("Workflow deleted successfully! Returning to playbooks...");
            setTimeout(() => {
                window.location.href = "/workflows";
            }, 1200);
        } catch (err) {
            console.error("Delete workflow error:", err);
            setToast("Failed to delete workflow: " + (err.response?.data?.error || err.message));
            setTimeout(() => setToast(null), 3500);
        }
    };

    // ─── Delete Node ───
    const handleDeleteNode = async (e, nodeId) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        if (!nodeId) return;

        const updatedNodes = (workflow?.nodes || []).filter(n => n.id !== nodeId);
        setWorkflow(prev => ({ ...prev, nodes: updatedNodes }));
        if (selectedNode?.id === nodeId) setSelectedNode(null);
        setToast("Block removed from canvas");
        setTimeout(() => setToast(null), 2500);

        try {
            const currentToken = localStorage.getItem("token") || token;
            await axios.put(
                `${backendURL}/api/respond/workflows/designer/${workflow?.id}`,
                {
                    nodes: updatedNodes,
                    name: workflow?.name,
                    enabled: workflow?.enabled,
                    version: workflow?.version || "1.3.0"
                },
                { headers: { Authorization: `Bearer ${currentToken}` } }
            );
        } catch (err) {
            console.error("Delete node error:", err);
            setToast("Warning: Could not sync node deletion to database");
            setTimeout(() => setToast(null), 3000);
        }
    };

    // ─── Update Node from Inspector ───
    const handleUpdateNode = async (updatedData) => {
        if (!selectedNode) return;
        const updatedNodes = (workflow?.nodes || []).map(n =>
            n.id === selectedNode.id ? { ...n, ...updatedData } : n
        );

        setWorkflow(prev => ({ ...prev, nodes: updatedNodes }));
        setSelectedNode(null);
        setToast("Node settings updated & saved!");
        setTimeout(() => setToast(null), 2500);

        try {
            const currentToken = localStorage.getItem("token") || token;
            await axios.put(
                `${backendURL}/api/respond/workflows/designer/${workflow?.id}`,
                {
                    nodes: updatedNodes,
                    name: workflow?.name,
                    enabled: workflow?.enabled,
                    version: workflow?.version || "1.3.0"
                },
                { headers: { Authorization: `Bearer ${currentToken}` } }
            );
        } catch (err) {
            console.error("Update node error:", err);
        }
    };

    const nodes = workflow?.nodes || [];

    // Colors mapping
    const getNodeStyle = (node) => {
        if (node.type === "trigger") {
            return "border-emerald-500/50 bg-emerald-950/40 text-emerald-300 shadow-emerald-900/20";
        }
        if (node.type === "condition") {
            return "border-rose-500/50 bg-rose-950/40 text-rose-300 shadow-rose-900/20";
        }
        if (node.branch === "true" || node.color === "purple") {
            return "border-purple-500/50 bg-purple-950/40 text-purple-300 shadow-purple-900/20";
        }
        if (node.branch === "false" || node.color === "blue") {
            return "border-blue-500/50 bg-blue-950/40 text-blue-300 shadow-blue-900/20";
        }
        return "border-white/[0.12] bg-[#0F172A] text-slate-200";
    };

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>04</span>
                <span>/</span>
                <Link to="/workflows" className="hover:text-blue-400">Workflows</Link>
                <span>/</span>
                <span className="text-slate-400">Workflow Designer</span>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (4 cols) ═══ */}
                <div className="lg:col-span-4 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            Build any response, conditions and all.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            Compose response in a visual designer, no code required. Drag and drop actions, then branch the logic with <strong>If / Else conditions, a Switch, or a For Each loop</strong>, so the same workflow can isolate a host, notify a team, or wait for approval depending on what the case actually is.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Interactive drag & drop canvas with free positioning.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Conditional branching with If / Else, Switch, and For Each.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Version, publish, and run, with a full execution history.</span>
                        </div>
                    </div>

                    {/* Quick Tools Overview */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[12px] font-bold text-white uppercase tracking-wider">Designer State</span>
                            <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                                Version {workflow?.version || "1.3.0"}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-[12px]">
                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-500 font-mono uppercase block">Active Nodes</span>
                                <span className="text-[18px] font-bold text-white font-mono">
                                    {nodes.length} Nodes
                                </span>
                            </div>
                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-500 font-mono uppercase block">Logic Branches</span>
                                <span className="text-[18px] font-bold text-rose-400 font-mono">
                                    {nodes.filter(n => n.type === "condition").length > 0 ? "2 Branches" : "1 Main Flow"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: Visual Designer Studio (8 cols) ═══ */}
                <div className="lg:col-span-8 bg-[#111827] border border-white/[0.06] rounded-2xl shadow-2xl overflow-hidden flex flex-col min-h-[720px]">
                    
                    {/* Top Ribbon Controls */}
                    <div className="p-3.5 border-b border-white/[0.06] bg-black/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        {/* Title & Status */}
                        <div className="flex items-center gap-3">
                            <span className="text-[11px] font-mono text-purple-400 uppercase font-bold bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                                QueryTel Studio
                            </span>
                            <h3 className="text-[13px] font-bold text-white truncate max-w-xs">
                                {workflow?.name}
                            </h3>
                            <div className="w-8 h-4.5 rounded-full bg-purple-600 relative p-0.5">
                                <div className="w-3.5 h-3.5 rounded-full bg-white translate-x-3.5" />
                            </div>
                        </div>

                        {/* Ribbon Action Buttons */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    setTargetBranch("main");
                                    setAddNodeType("action");
                                }}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white rounded-lg text-[11px] font-bold border border-blue-500/30 transition-all"
                            >
                                <Plus className="w-3 h-3" />
                                Action
                            </button>
                            <button
                                onClick={() => {
                                    setTargetBranch("main");
                                    setAddNodeType("condition");
                                }}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-600/20 hover:bg-rose-600 text-rose-300 hover:text-white rounded-lg text-[11px] font-bold border border-rose-500/30 transition-all"
                            >
                                <GitBranch className="w-3 h-3" />
                                Condition
                            </button>
                            <button
                                onClick={handleSaveDraft}
                                className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[11px] font-medium transition-all"
                            >
                                <Save className="w-3 h-3" />
                                Save Draft
                            </button>
                            <button
                                onClick={handlePublish}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-lg text-[11.5px] font-bold shadow-md shadow-purple-600/20 transition-all"
                            >
                                <Zap className="w-3 h-3" />
                                Publish
                            </button>
                            <button
                                onClick={handleDeleteWorkflow}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-600/20 hover:bg-rose-600 text-rose-300 hover:text-white rounded-lg text-[11px] font-bold border border-rose-500/30 transition-all"
                                title="Delete entire workflow playbook"
                            >
                                <Trash2 className="w-3 h-3" />
                                Delete
                            </button>
                        </div>
                    </div>

                    {/* Designer Studio Body: Split into AI Copilot Sidebar & Flowchart Canvas */}
                    <div className="flex-1 flex flex-col md:flex-row relative">
                        
                        {/* ─── QueryTel Workflow Assistant Copilot Panel ─── */}
                        {copilotOpen && (
                            <div className="w-full md:w-72 bg-[#0C1019] border-r border-white/[0.06] flex flex-col p-3.5 space-y-3 z-20">
                                <div className="flex items-center justify-between border-b border-white/[0.06] pb-2">
                                    <div className="flex items-center gap-1.5 text-[11.5px] font-bold text-white">
                                        <Bot className="w-3.5 h-3.5 text-purple-400" />
                                        Workflow Assistant
                                    </div>
                                    <button onClick={() => setCopilotOpen(false)} className="text-slate-500 hover:text-white">
                                        <ChevronLeft className="w-3.5 h-3.5" />
                                    </button>
                                </div>

                                <p className="text-[10.5px] text-slate-400">
                                    Ask me about this workflow, or click a suggestion:
                                </p>

                                {/* Prompt Suggestion Chips */}
                                <div className="space-y-1.5">
                                    {[
                                        "How do I raise priority when Malicious?",
                                        "Assign the case to Focus automatically",
                                        "When are cases marked as closed?"
                                    ].map((chip, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => handleSendMessage(chip)}
                                            className="w-full text-left p-2 rounded-lg bg-black/40 hover:bg-purple-950/30 text-slate-300 hover:text-white border border-white/[0.04] text-[10px] transition-colors leading-tight"
                                        >
                                            {chip}
                                        </button>
                                    ))}
                                </div>

                                {/* Chat Messages Stream */}
                                <div className="flex-1 overflow-y-auto max-h-56 space-y-2 pr-1 custom-scrollbar text-[11px]">
                                    {messages.map((m, idx) => (
                                        <div
                                            key={idx}
                                            className={`p-2.5 rounded-xl space-y-1.5 ${
                                                m.role === "assistant"
                                                    ? "bg-purple-950/20 border border-purple-500/20 text-slate-200"
                                                    : "bg-blue-600 text-white ml-4"
                                            }`}
                                        >
                                            <p className="whitespace-pre-line leading-relaxed">{m.content}</p>
                                            {m.suggestedAction && (
                                                <button
                                                    onClick={() => handleAddNode({
                                                        title: m.suggestedAction.node,
                                                        subtitle: "Workflow Action",
                                                        desc: "Auto-inserted via AI Workflow Assistant",
                                                        type: "action",
                                                        color: "purple"
                                                    })}
                                                    className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded text-[10px] font-bold shadow transition-colors mt-1"
                                                >
                                                    <Plus className="w-3 h-3" />
                                                    Insert Node into Canvas
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {copilotLoading && (
                                        <div className="text-[10px] font-mono text-purple-400 animate-pulse">
                                            QueryTel Assistant is thinking...
                                        </div>
                                    )}
                                </div>

                                {/* Copilot Input */}
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }}
                                    className="flex items-center gap-1.5 pt-2 border-t border-white/[0.06]"
                                >
                                    <input
                                        type="text"
                                        placeholder="Ask about your workflow..."
                                        value={inputPrompt}
                                        onChange={(e) => setInputPrompt(e.target.value)}
                                        className="flex-1 bg-black/50 border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-[11px] text-white outline-none focus:border-purple-500"
                                    />
                                    <button
                                        type="submit"
                                        className="p-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
                                    >
                                        <Send className="w-3 h-3" />
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* Collapsed Copilot Button if closed */}
                        {!copilotOpen && (
                            <button
                                onClick={() => setCopilotOpen(true)}
                                className="absolute top-3 left-3 z-30 p-2 bg-[#111827] border border-white/[0.08] rounded-xl text-purple-400 hover:text-white shadow-lg flex items-center gap-1 text-[11px] font-bold"
                            >
                                <Bot className="w-3.5 h-3.5" />
                                Open Assistant
                            </button>
                        )}

                        {/* ─── Interactive Drag & Drop Flowchart Canvas ─── */}
                        <div
                            ref={canvasRef}
                            onMouseMove={handleCanvasMouseMove}
                            onMouseUp={handleCanvasMouseUp}
                            onMouseLeave={handleCanvasMouseUp}
                            className="flex-1 relative bg-[#0B0F17] overflow-auto custom-scrollbar min-h-[600px] select-none"
                            style={{ cursor: draggingNodeId ? "grabbing" : "default" }}
                        >
                            {/* Dot Grid Background */}
                            <div
                                className="absolute inset-0 opacity-20 pointer-events-none"
                                style={{
                                    backgroundImage: "radial-gradient(#94A3B8 1px, transparent 1px)",
                                    backgroundSize: "20px 20px"
                                }}
                            />

                            {/* Canvas Instructions Banner */}
                            <div className="absolute top-3 right-3 z-10 bg-black/60 border border-white/[0.06] rounded-xl px-3 py-1.5 text-[10.5px] font-mono text-slate-400 backdrop-blur-sm flex items-center gap-2 pointer-events-none">
                                <Move className="w-3 h-3 text-purple-400" />
                                Drag any block to reposition • Click to inspect
                            </div>

                            {/* Dynamic SVG Connecting Lines between Nodes */}
                            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" style={{ transform: `scale(${zoom})`, transformOrigin: "0 0" }}>
                                {nodes.map((node) => {
                                    // If this is not a trigger, find its parent and draw a wire
                                    if (node.type === "trigger") return null;

                                    let parent = null;
                                    if (node.branch === "true" || node.branch === "false") {
                                        parent = nodes.find(n => n.type === "condition") || nodes[0];
                                    } else {
                                        const nodeIdx = nodes.findIndex(n => n.id === node.id);
                                        parent = nodeIdx > 0 ? nodes[nodeIdx - 1] : null;
                                    }

                                    if (!parent) return null;

                                    const startX = (parent.x || 260) + 120;
                                    const startY = (parent.y || 100) + 60;
                                    const endX = (node.x || 260) + 120;
                                    const endY = (node.y || 200);

                                    const isTrueBranch = node.branch === "true";
                                    const isFalseBranch = node.branch === "false";
                                    const strokeColor = isTrueBranch ? "#A855F7" : isFalseBranch ? "#3B82F6" : "#64748B";

                                    return (
                                        <g key={`wire-${node.id}`}>
                                            <path
                                                d={`M ${startX} ${startY} C ${startX} ${startY + (endY - startY) / 2}, ${endX} ${startY + (endY - startY) / 2}, ${endX} ${endY}`}
                                                fill="none"
                                                stroke={strokeColor}
                                                strokeWidth="2"
                                                strokeDasharray={node.type === "condition" || node.branch ? "4 2" : "none"}
                                                opacity="0.75"
                                            />
                                            {/* Wire End Dot */}
                                            <circle cx={endX} cy={endY} r="3" fill={strokeColor} />
                                        </g>
                                    );
                                })}
                            </svg>

                            {/* Render All Nodes Interactively with Drag & Drop */}
                            <div
                                className="relative w-[1000px] h-[900px] p-6 transition-transform duration-75"
                                style={{ transform: `scale(${zoom})`, transformOrigin: "0 0" }}
                            >
                                {nodes.map((node) => {
                                    const styleClass = getNodeStyle(node);
                                    const isDragging = draggingNodeId === node.id;
                                    const isTrigger = node.type === "trigger";
                                    const isCondition = node.type === "condition";

                                    return (
                                        <div
                                            key={node.id}
                                            style={{
                                                position: "absolute",
                                                left: `${node.x || 260}px`,
                                                top: `${node.y || 100}px`,
                                                width: isCondition ? "280px" : "240px",
                                                zIndex: isDragging ? 40 : 20
                                            }}
                                            onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                                            onClick={() => setSelectedNode(node)}
                                            className={`rounded-2xl border ${styleClass} backdrop-blur-md shadow-2xl p-3.5 cursor-grab active:cursor-grabbing transition-all group hover:border-white/50 hover:shadow-purple-500/10 ${
                                                isDragging ? "ring-2 ring-purple-500/80 scale-105" : ""
                                            }`}
                                        >
                                            {/* Node Header */}
                                            <div className="flex items-center justify-between mb-1.5">
                                                <div className="flex items-center gap-2">
                                                    <GripVertical className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 cursor-grab" />
                                                    <div className="w-5 h-5 rounded-md bg-black/40 flex items-center justify-center font-bold text-[10px]">
                                                        {isTrigger && <Zap className="w-3 h-3 text-emerald-400" />}
                                                        {isCondition && <GitBranch className="w-3 h-3 text-rose-400" />}
                                                        {!isTrigger && !isCondition && <ShieldCheck className="w-3 h-3 text-blue-400" />}
                                                    </div>
                                                    <h4 className="text-[12px] font-bold text-white tracking-tight truncate max-w-[130px]">
                                                        {node.title}
                                                    </h4>
                                                </div>

                                                <div className="flex items-center gap-1">
                                                    <span className="text-[8px] font-mono uppercase bg-white/[0.06] text-slate-300 px-1.5 py-0.5 rounded">
                                                        {node.subtitle || node.type}
                                                    </span>
                                                    {!isTrigger && (
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteNode(e, node.id);
                                                            }}
                                                            className="p-1 hover:bg-rose-600 text-rose-400 hover:text-white rounded transition-all"
                                                            title="Delete node"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Node Description */}
                                            <p className="text-[10px] text-slate-300 pl-5 leading-tight line-clamp-2">
                                                {node.description}
                                            </p>

                                            {/* Branch Labels if Condition */}
                                            {isCondition && (
                                                <div className="mt-2.5 pl-5 flex items-center gap-1.5 text-[9px] font-mono">
                                                    <span className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30 font-bold">
                                                        ✓ True (Malicious)
                                                    </span>
                                                    <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30 font-bold">
                                                        ✗ False (Benign)
                                                    </span>
                                                </div>
                                            )}

                                            {/* Branch Indicator for Actions */}
                                            {node.branch && (
                                                <div className="mt-2 pl-5">
                                                    <span className={`text-[8.5px] font-mono font-bold px-1.5 py-0.5 rounded ${
                                                        node.branch === "true"
                                                            ? "bg-purple-500/20 text-purple-300"
                                                            : "bg-blue-500/20 text-blue-300"
                                                    }`}>
                                                        Branch: {node.branch === "true" ? "True (Malicious)" : "False (Benign)"}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Bottom-Left Zoom Controls */}
                            <div className="absolute bottom-4 left-4 z-20 flex items-center gap-1 bg-[#111827] border border-white/[0.08] rounded-xl p-1 shadow-lg text-slate-300 text-[11px]">
                                <button
                                    onClick={() => setZoom(prev => Math.max(0.6, prev - 0.1))}
                                    className="p-1.5 hover:bg-white/[0.08] rounded-lg"
                                    title="Zoom Out"
                                >
                                    <ZoomOut className="w-3.5 h-3.5" />
                                </button>
                                <span className="px-2 font-mono">{Math.round(zoom * 100)}%</span>
                                <button
                                    onClick={() => setZoom(prev => Math.min(1.4, prev + 0.1))}
                                    className="p-1.5 hover:bg-white/[0.08] rounded-lg"
                                    title="Zoom In"
                                >
                                    <ZoomIn className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    onClick={() => setZoom(1)}
                                    className="p-1.5 hover:bg-white/[0.08] rounded-lg"
                                    title="Reset Zoom"
                                >
                                    <Maximize2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>

                    </div>

                </div>

            </div>

            {/* ─── Node Inspector Modal ─── */}
            {selectedNode && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-md p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[14px] font-bold text-white flex items-center gap-2">
                                <Sliders className="w-4 h-4 text-purple-400" />
                                Node Inspector: {selectedNode.title}
                            </h3>
                            <button onClick={() => setSelectedNode(null)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                const form = e.target;
                                handleUpdateNode({
                                    title: form.title.value,
                                    description: form.description.value,
                                    connector: form.connector.value,
                                    branch: form.branch.value !== "none" ? form.branch.value : null
                                });
                            }}
                            className="space-y-3"
                        >
                            <div>
                                <label className="text-slate-400 block mb-1 font-mono text-[10.5px]">Node Title</label>
                                <input
                                    name="title"
                                    type="text"
                                    defaultValue={selectedNode.title}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-1.5 text-white outline-none focus:border-purple-500"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1 font-mono text-[10.5px]">Target Connector</label>
                                <input
                                    name="connector"
                                    type="text"
                                    defaultValue={selectedNode.connector || selectedNode.subtitle || "FortiGate Firewall"}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-1.5 text-white outline-none font-mono text-[11px]"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1 font-mono text-[10.5px]">Attached Branch</label>
                                <select
                                    name="branch"
                                    defaultValue={selectedNode.branch || "none"}
                                    className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-1.5 text-white outline-none"
                                >
                                    <option value="none">Main Sequence Flow</option>
                                    <option value="true">Branch: True (Malicious)</option>
                                    <option value="false">Branch: False (Benign)</option>
                                </select>
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1 font-mono text-[10.5px]">Description / Output</label>
                                <textarea
                                    name="description"
                                    rows="2"
                                    defaultValue={selectedNode.description}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-1.5 text-slate-200 outline-none text-[11px]"
                                />
                            </div>

                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04] space-y-1 font-mono text-[11px]">
                                <span className="text-slate-400 block">Execution Mode: Synchronous Zero-Trust Gate</span>
                                <span className="text-emerald-400 block">✓ Error Handling: Auto-Retry (3 attempts)</span>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
                                {selectedNode.type !== "trigger" ? (
                                    <button
                                        type="button"
                                        onClick={(e) => handleDeleteNode(e, selectedNode.id)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600/20 hover:bg-rose-600 text-rose-300 hover:text-white rounded-lg font-bold border border-rose-500/30 transition-all text-[11px]"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        Delete Node
                                    </button>
                                ) : <div />}

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedNode(null)}
                                        className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[11px]"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md text-[11px]"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ─── Add Node Modal ─── */}
            {addNodeType && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-md p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[14px] font-bold text-white">
                                {addNodeType === "action" ? "Insert Response Action Node" : "Insert Logic Condition Node"}
                            </h3>
                            <button onClick={() => setAddNodeType(null)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Branch Placement Selector */}
                        <div>
                            <label className="text-slate-400 block mb-1 font-mono text-[10.5px]">Where to insert this block?</label>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { id: "main", label: "Main Sequence" },
                                    { id: "true", label: "True (Malicious)" },
                                    { id: "false", label: "False (Benign)" }
                                ].map((b) => (
                                    <button
                                        key={b.id}
                                        type="button"
                                        onClick={() => setTargetBranch(b.id)}
                                        className={`p-2 rounded-lg border text-center text-[11px] font-bold transition-all ${
                                            targetBranch === b.id
                                                ? "bg-purple-600 border-purple-400 text-white shadow-md"
                                                : "bg-black/30 border-white/[0.06] text-slate-400 hover:text-white"
                                        }`}
                                    >
                                        {b.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-2">
                            {addNodeType === "action" ? (
                                [
                                    { title: "Block IP on FortiGate", subtitle: "FortiGate Firewall", desc: "Stage perimeter address block in Approvals Queue" },
                                    { title: "Quarantine Host at Gateway", subtitle: "FortiOS Perimeter", desc: "Isolate internal IP to quarantine VLAN" },
                                    { title: "Block Malicious Domain / URL", subtitle: "FortiGate Web Filter", desc: "Enforce DNS and web content filtering across edge firewalls" },
                                    { title: "Export Forensic Log Bundle", subtitle: "FortiAnalyzer Core", desc: "Extract raw syslog evidence packet from FortiAnalyzer" },
                                    { title: "Create Zoho Desk Incident Ticket", subtitle: "Zoho Desk", desc: "Dispatch ticket to Dept #388402000000006907" }
                                ].map((item, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleAddNode({
                                            title: item.title,
                                            subtitle: item.subtitle,
                                            description: item.desc,
                                            type: "action",
                                            color: "purple"
                                        })}
                                        className="w-full p-2.5 bg-black/30 hover:bg-purple-950/30 border border-white/[0.04] rounded-xl text-left transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-bold text-white text-[12px]">{item.title}</span>
                                            <span className="text-[9px] font-mono text-purple-300">{item.subtitle}</span>
                                        </div>
                                        <p className="text-[10.5px] text-slate-400 mt-0.5">{item.desc}</p>
                                    </button>
                                ))
                            ) : (
                                [
                                    { title: "If / Else Condition", subtitle: "Branching", desc: "Evaluate Boolean condition (Verdict == Malicious)" },
                                    { title: "Switch (Multi-Branch)", subtitle: "Case Match", desc: "Branch by Severity (Critical, High, Medium, Low)" },
                                    { title: "For Each Loop", subtitle: "Array Iterator", desc: "Iterate across list of recipients or infected hosts" },
                                    { title: "Human Approval Gate", subtitle: "SecOps Gate", desc: "Pause workflow until SOC analyst approves" }
                                ].map((item, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleAddNode({
                                            title: item.title,
                                            subtitle: item.subtitle,
                                            description: item.desc,
                                            type: "condition",
                                            color: "rose"
                                        })}
                                        className="w-full p-2.5 bg-black/30 hover:bg-rose-950/30 border border-white/[0.04] rounded-xl text-left transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-bold text-white text-[12px]">{item.title}</span>
                                            <span className="text-[9px] font-mono text-rose-300">{item.subtitle}</span>
                                        </div>
                                        <p className="text-[10.5px] text-slate-400 mt-0.5">{item.desc}</p>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
